/**
 * useDimensionFilters — multi-select dimension filters with URL persistence.
 *
 * Tracks a fixed set of dimension keys and stores selections in the URL as
 * `dim_<key>=v1,v2` so they survive refresh/share/back (like useTimeFilter).
 *
 * Returns:
 *   selected     — { dimKey: [values] } (only non-empty dims), for the API/context
 *   setDimension(key, values) — replace one dimension's selection
 *   clearAll()   — clear every tracked dimension
 */
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export function useDimensionFilters({ dimensions = [], paramPrefix = 'dim_' } = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const keysSig = dimensions.join('|');

  const selected = useMemo(() => {
    const out = {};
    for (const key of dimensions) {
      const raw = searchParams.get(`${paramPrefix}${key}`);
      if (raw) {
        const vals = raw.split(',').map((v) => v.trim()).filter(Boolean);
        if (vals.length) out[key] = vals;
      }
    }
    return out;
  }, [searchParams, keysSig, paramPrefix]);

  const setDimension = useCallback((key, values) => {
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      if (values && values.length) sp.set(`${paramPrefix}${key}`, values.join(','));
      else sp.delete(`${paramPrefix}${key}`);
      return sp;
    }, { replace: true });
  }, [setSearchParams, paramPrefix]);

  const clearAll = useCallback(() => {
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      dimensions.forEach((key) => sp.delete(`${paramPrefix}${key}`));
      return sp;
    }, { replace: true });
  }, [setSearchParams, keysSig, paramPrefix, dimensions]);

  return { selected, setDimension, clearAll };
}

export default useDimensionFilters;
